"use client";

import { AlertTriangle, CheckCircle2, Loader2, Mic, Play, Square, Volume2 } from "lucide-react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { Button } from "@/app/_module/components/ui/button";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/_module/components/ui/select";
import { Switch } from "@/app/_module/components/ui/switch";
import { TwilioDeviceContext } from "@/app/_module/contexts/TwilioDeviceContext";
import { canSoftenLoudCalls, setSoftenLoudCalls, softenLoudCallsOn } from "@/app/_module/utils/loud-call-limiter";
import { requestMicrophoneWithGuidance } from "@/app/_module/utils/microphone-permission";

// No sound this long while testing = probably muted, or the wrong microphone.
const SILENCE_MS = 5000;
// Meter level (0–1) that counts as "we can hear you".
const HEARD_LEVEL = 0.08;

/**
 * Check the headset before dialing (Settings → Preferences → Dialer). The call panel's Audio Devices
 * meter only works on a live call — it samples the call. Lists and picks are the voice context's, the
 * ones the call panel shows, so a pick here is what the next call uses (until the page reloads).
 */
export function AudioCheckCard() {
  const voice = useContext(TwilioDeviceContext);
  const inputs = voice?.audioInputDevices ?? [];
  const outputs = voice?.audioOutputDevices ?? [];
  // A pick shows at once; the context's copy follows when the device switch lands.
  const [pickedInput, setPickedInput] = useState<string | null>(null);
  const [pickedOutput, setPickedOutput] = useState<string | null>(null);
  const inputId = pickedInput ?? voice?.selectedInputDeviceId ?? "default";
  const outputId = pickedOutput ?? voice?.selectedOutputDeviceId ?? "default";
  const [testing, setTesting] = useState<"off" | "starting" | "on">("off");
  const [micError, setMicError] = useState<string | null>(null);
  const [heard, setHeard] = useState(false);
  const [silent, setSilent] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [speakerError, setSpeakerError] = useState<string | null>(null);
  const [canPickOutput, setCanPickOutput] = useState(false);
  const [soften, setSoften] = useState(false);
  const [canSoften, setCanSoften] = useState(true);
  const barRef = useRef<HTMLDivElement>(null);
  const stopRef = useRef<(() => void) | null>(null);
  const refreshDevices = voice?.refreshAudioDevices;
  const listed = inputs.length > 0;

  useEffect(() => {
    // After mount: the server render can't know, and guessing there breaks hydration.
    setCanPickOutput("setSinkId" in HTMLMediaElement.prototype);
    setSoften(softenLoudCallsOn());
    setCanSoften(canSoftenLoudCalls());
    return () => stopRef.current?.();
  }, []);

  useEffect(() => {
    // A headset plugged in mid-check shows up without pressing Test again.
    const md = navigator.mediaDevices;
    if (!listed || !refreshDevices || !md?.addEventListener) return;
    const onChange = () => void refreshDevices();
    md.addEventListener("devicechange", onChange);
    return () => md.removeEventListener("devicechange", onChange);
  }, [listed, refreshDevices]);

  const stopTest = useCallback(() => {
    stopRef.current?.();
    stopRef.current = null;
    setTesting("off");
  }, []);

  const startTest = useCallback(async (deviceId: string) => {
    stopRef.current?.();
    stopRef.current = null;
    setMicError(null);
    setHeard(false);
    setSilent(false);
    setTesting("starting");
    const permission = await requestMicrophoneWithGuidance();
    if (!permission.granted) {
      setMicError(permission.error || "Microphone access is blocked for this site.");
      setTesting("off");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        // The processing calls use, so the bar shows what the person on the line would hear.
        audio: {
          ...(deviceId !== "default" && { deviceId: { exact: deviceId } }),
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      // Fills the context's lists (device names need permission). The "Microphone Changed" toast
      // names a pick from that list, so only devices listed there are offered.
      void refreshDevices?.();
      const ctx = new AudioContext();
      // Created after the permission prompt, so the click no longer counts as the gesture audio needs.
      if (ctx.state === "suspended") await ctx.resume();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const samples = new Float32Array(analyser.fftSize);
      let level = 0;
      let frame = 0;
      let lastSound = performance.now();
      let wasHeard = false;
      let wasSilent = false;
      const tick = () => {
        analyser.getFloatTimeDomainData(samples);
        let sum = 0;
        for (const s of samples) sum += s * s;
        // Speech RMS sits around 0.02–0.2: scale it up, let it fall back slowly like a VU meter.
        level = Math.max(Math.min(1, Math.sqrt(sum / samples.length) * 6), level * 0.9);
        if (barRef.current) barRef.current.style.width = `${Math.round(level * 100)}%`;
        const now = performance.now();
        if (level >= HEARD_LEVEL) {
          lastSound = now;
          if (!wasHeard) setHeard((wasHeard = true));
          if (wasSilent) setSilent((wasSilent = false));
        } else if (!wasSilent && now - lastSound > SILENCE_MS) {
          setSilent((wasSilent = true));
        }
        frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
      stopRef.current = () => {
        cancelAnimationFrame(frame);
        stream.getTracks().forEach((t) => t.stop());
        void ctx.close();
        if (barRef.current) barRef.current.style.width = "0%";
      };
      setTesting("on");
    } catch (e: any) {
      setMicError(
        e?.name === "NotReadableError"
          ? "That microphone is busy in another app (Zoom, Teams…). Close it and test again."
          : e?.name === "NotFoundError" || e?.name === "OverconstrainedError"
            ? "That microphone isn't connected anymore. Pick another one."
            : "Couldn't start the microphone. Try another one.",
      );
      setTesting("off");
    }
  }, [refreshDevices]);

  const pickInput = (id: string) => {
    setPickedInput(id);
    void voice?.setAudioInputDevice(id);
    if (testing !== "off") void startTest(id);
  };

  const pickOutput = (id: string) => {
    setPickedOutput(id);
    setSpeakerError(null);
    void voice?.setAudioOutputDevice(id);
  };

  const playTestSound = async () => {
    setSpeakerError(null);
    setPlaying(true);
    try {
      const audio = new Audio("/sounds/notification.wav");
      if (canPickOutput && outputId !== "default") await (audio as any).setSinkId(outputId);
      audio.onended = () => setPlaying(false);
      await audio.play();
    } catch {
      setPlaying(false);
      setSpeakerError("Couldn't play through that device. Pick another one.");
    }
  };

  const listedInput = inputs.some((d) => d.deviceId === inputId) ? inputId : undefined;
  const listedOutput = outputs.some((d) => d.deviceId === outputId) ? outputId : undefined;

  return (
    <Card className="overflow-hidden mb-6">
      <CardContent className="p-6 space-y-4">
        <div>
          <h2 className="!text-lg font-semibold tracking-tight">Microphone &amp; speaker check</h2>
          <p className="text-sm text-muted-foreground">
            Test your headset before you dial. A device you pick here is used for your next call.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Mic className="h-4 w-4 text-muted-foreground" aria-hidden />
              Microphone
            </div>
            <Select value={listedInput} onValueChange={pickInput} disabled={!listed}>
              <SelectTrigger aria-label="Microphone">
                <SelectValue placeholder={listed ? "System default" : "Press Test to see your microphones"} />
              </SelectTrigger>
              <SelectContent>
                {inputs.map((d) => (
                  <SelectItem key={d.deviceId} value={d.deviceId}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
              <div
                ref={barRef}
                className={`h-full rounded-full transition-[width] duration-75 ${silent ? "bg-amber-500" : "bg-emerald-500"}`}
                style={{ width: "0%" }}
              />
            </div>
            <div className="flex items-start justify-between gap-3">
              <p role="status" className="min-h-[2rem] text-xs leading-4">
                {micError ? (
                  <span className="flex gap-1.5 whitespace-pre-line text-destructive"><AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />{micError}</span>
                ) : testing === "on" && silent ? (
                  <span className="flex gap-1.5 text-amber-700"><AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />We can&apos;t hear anything. Check your headset&apos;s mute switch, or pick another microphone.</span>
                ) : testing === "on" && heard ? (
                  <span className="flex gap-1.5 text-emerald-700"><CheckCircle2 className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />We can hear you.</span>
                ) : testing === "on" ? (
                  <span className="text-muted-foreground">Listening… say something.</span>
                ) : (
                  <span className="text-muted-foreground">Press Test, then talk. The bar should move.</span>
                )}
              </p>
              {testing === "on" ? (
                <Button size="sm" variant="outline" onClick={stopTest}>
                  <Square className="h-3.5 w-3.5" aria-hidden />
                  Stop
                </Button>
              ) : (
                <Button size="sm" onClick={() => void startTest(inputId)} disabled={testing === "starting"}>
                  {testing === "starting" ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Mic className="h-3.5 w-3.5" aria-hidden />}
                  Test
                </Button>
              )}
            </div>
          </div>

          <div className="rounded-lg border p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Volume2 className="h-4 w-4 text-muted-foreground" aria-hidden />
              Speaker / headphones
            </div>
            {canPickOutput ? (
              <Select value={listedOutput} onValueChange={pickOutput} disabled={outputs.length === 0}>
                <SelectTrigger aria-label="Speaker or headphones">
                  <SelectValue placeholder={outputs.length ? "System default" : "Press Test on the microphone first"} />
                </SelectTrigger>
                <SelectContent>
                  {outputs.map((d) => (
                    <SelectItem key={d.deviceId} value={d.deviceId}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xs text-muted-foreground">This browser plays calls through your computer&apos;s sound settings.</p>
            )}
            <div className="flex items-start justify-between gap-3">
              <p role="status" className="min-h-[2rem] text-xs leading-4">
                {speakerError ? (
                  <span className="flex gap-1.5 text-destructive"><AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />{speakerError}</span>
                ) : (
                  <span className="text-muted-foreground">You should hear a chime in your headset.</span>
                )}
              </p>
              <Button size="sm" variant="outline" onClick={() => void playTestSound()} disabled={playing}>
                {playing ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Play className="h-3.5 w-3.5" aria-hidden />}
                Play sound
              </Button>
            </div>
            <div className="flex items-start justify-between gap-3 border-t pt-3">
              <label htmlFor="soften-loud-calls" className="space-y-0.5">
                <span className="block text-sm font-medium">Soften loud calls</span>
                <span className="block text-xs text-muted-foreground">
                  {canSoften
                    ? "Turns down loud voicemail greetings, beeps and tones. Normal talking stays the same. Saved locally."
                    : "Only available in Chrome or Edge on a computer or Android."}
                </span>
              </label>
              <Switch
                id="soften-loud-calls"
                checked={canSoften && soften}
                disabled={!canSoften}
                onCheckedChange={(on) => {
                  setSoften(on);
                  setSoftenLoudCalls(on);
                }}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
